import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Cookies from 'js-cookie';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  /** Raw HttpClient that bypasses all interceptors – used only for the SSO logout call
   *  so that a 401 response from the backend never triggers the token-refresh interceptor. */
  private bypassHttp: HttpClient;

  /** Guard to prevent concurrent globalLogout() invocations (e.g. from interceptor + user click). */
  private isLoggingOut = false;

  constructor(private http: HttpClient, handler: HttpBackend) {
    this.bypassHttp = new HttpClient(handler);
  }

  private companyIdKey = 'company_id';

  // LOGOUT PROCESS
  // async silentLogoutAll(urls): Promise<void> {
  //   const currentOrigin = window.location.origin;

  //   for (const url of urls) {
  //     // Skip if URL belongs to current app
  //     if (url.startsWith(currentOrigin)) {
  //       console.log("Skipping own URL:", url);
  //       continue;
  //     }

  //     await this.openIframeForOneSecond(url);
  //   }
  // }

  async silentLogoutAll(urls: string[]): Promise<any[]> {
    const currentOrigin = window.location.origin;
    const results: any[] = [];

    const tasks = urls
      .filter((url) => {
        if (url.startsWith(currentOrigin)) {
          console.log("Skipping own URL:", url);
          return false;
        }
        return true;
      })
      .map(async (url) => {
        try {
          // Wait indefinitely for iframe to load or error natively, with 2 retries
          await this.openIframeWithRetry(url, 0);
          results.push({ url, status: "success" });
        } catch (err) {
          results.push({ url, status: "failed", error: err.message });
          console.warn(`Silent logout failed for ${url}:`, err.message);
        }
      });

    // ── Execute in parallel and wait for all (No global cap) ──────────────────
    await Promise.allSettled(tasks);

    return results;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((res) => setTimeout(res, ms));
  }

  private async openIframeWithRetry(url: string, retries = 2, retryDelay = 500): Promise<void> {
    let lastError;

    for (let attempt = 0; attempt <= retries; attempt++) {
      if (attempt > 0) {
        console.warn(`Retrying silent logout for ${url} (attempt ${attempt + 1})`);
        await this.wait(retryDelay);
      }

      try {
        await this.openIframeAndWaitForSignal(url);
        return; // success
      } catch (err) {
        lastError = err;
        console.warn(err.message);
      }
    }
    throw lastError;
  }

  private openIframeAndWaitForSignal(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";

      const iframeOrigin = new URL(url).origin;
      let settled = false;

      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== iframeOrigin) return;
        if (event.data === "SILENT_LOGOUT_DONE") {
          cleanup();
          resolve();
        }
      };

      const cleanup = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener("message", messageHandler);
        iframe.remove();
      };

      iframe.onerror = () => {
        cleanup();
        reject(new Error(`Failed to load iframe for ${url}`));
      };

      window.addEventListener("message", messageHandler);
      document.body.appendChild(iframe);
      iframe.src = url;
    });
  }


  private showLogoutLoader(): void {
    if (document.getElementById('cb-logout-overlay')) return;

    const style = document.createElement('style');
    style.id = 'cb-logout-style';
    style.textContent = `
      #cb-logout-overlay {
        position: fixed;
        inset: 0;
        background: rgba(255, 255, 255, 0.97);
        z-index: 999999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 20px;
        backdrop-filter: blur(2px);
      }
      .cb-logout-spinner {
        width: 48px;
        height: 48px;
        border: 4px solid #e9d5ff;
        border-top-color: #8F00FF;
        border-radius: 50%;
        animation: cb-spin 0.8s linear infinite;
      }
      @keyframes cb-spin { to { transform: rotate(360deg); } }
      .cb-logout-text {
        font-family: Inter, system-ui, sans-serif;
        font-size: 15px;
        font-weight: 500;
        color: #334155;
        letter-spacing: 0.01em;
      }
      .cb-logout-subtext {
        font-family: Inter, system-ui, sans-serif;
        font-size: 13px;
        color: #94a3b8;
        margin-top: -12px;
      }
      #cb-logout-error-toast {
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 9999999;
        background: #fff;
        border: 1px solid #fecaca;
        border-left: 4px solid #ef4444;
        border-radius: 10px;
        padding: 14px 18px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.12);
        display: flex;
        align-items: flex-start;
        gap: 12px;
        max-width: 340px;
        font-family: Inter, system-ui, sans-serif;
        animation: cb-toast-in 0.25s ease;
      }
      @keyframes cb-toast-in {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .cb-toast-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
      .cb-toast-body { flex: 1; }
      .cb-toast-title { font-size: 14px; font-weight: 600; color: #0f172a; margin: 0 0 2px; }
      .cb-toast-msg { font-size: 13px; color: #64748b; margin: 0; }
      .cb-toast-close {
        background: none; border: none; cursor: pointer;
        color: #94a3b8; font-size: 16px; padding: 0; line-height: 1; flex-shrink: 0;
      }
      .cb-toast-close:hover { color: #475569; }
    `;
    document.head.appendChild(style);

    const overlay = document.createElement('div');
    overlay.id = 'cb-logout-overlay';
    overlay.innerHTML = `
      <div class="cb-logout-spinner"></div>
      <p class="cb-logout-text">Signing you out…</p>
      <p class="cb-logout-subtext">Please wait a moment</p>
    `;
    document.body.appendChild(overlay);
  }

  private hideLogoutLoader(): void {
    document.getElementById('cb-logout-overlay')?.remove();
  }

  private showLogoutError(message = 'Sign out failed. Please try again.'): void {
    this.hideLogoutLoader();

    const existing = document.getElementById('cb-logout-error-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'cb-logout-error-toast';
    toast.innerHTML = `
      <span class="cb-toast-icon">⚠️</span>
      <div class="cb-toast-body">
        <p class="cb-toast-title">Sign Out Failed</p>
        <p class="cb-toast-msg">${message}</p>
      </div>
      <button class="cb-toast-close" onclick="this.parentElement.remove()">✕</button>
    `;
    document.body.appendChild(toast);

    setTimeout(() => toast.remove(), 6000);
  }

  private FALLBACK_SILENT_LOGOUT_URLS = environment.silentLogoutUrls;

  async globalLogout(showLoader = false) {
    // ── Guard: prevent concurrent invocations (e.g. interceptor + user click racing) ──
    if (this.isLoggingOut) {
      console.warn('globalLogout() already in progress – skipping duplicate call.');
      return;
    }
    this.isLoggingOut = true;

    if (showLoader) this.showLogoutLoader();

    let ssoSession = Cookies.get("sso_session");
    if (!ssoSession) {
      ssoSession = localStorage.getItem("sso_session_id");
    }

    const performFinalCleanup = async (urls: string[]) => {
      this.removeId();
      this.removeToken();
      localStorage.removeItem('CSRF');
      localStorage.removeItem('sso_session_id');

      // ⛔ WAIT UNTIL ALL LOGOUT URLS ARE PROCESSED
      await this.silentLogoutAll(urls);

      // Remove shared SSO cookie after background logouts
      Cookies.remove("sso_session", { domain: ".cloudbalance.in", path: "/" });

      console.log('All URLs Processed');

      // ⛔ Only now generate CSRF + redirect
      let id = crypto.randomUUID();
      localStorage.setItem('CSRF', id);

      const baseUrl = window.location.origin;
      window.location.href = environment.ssoApiUrl + 'sso/authorize?client_id=cb-sales&redirect_uri=' + baseUrl + '/auth/callback&state=' + id + '&prompt=none';
    };

    // If NO session ID found, skip backend call but still do iframes + cleanup
    if (!ssoSession || ssoSession === 'undefined') {
      console.warn("No active SSO session found. Skipping backend API, performing fallback cleanup.");
      await performFinalCleanup(this.FALLBACK_SILENT_LOGOUT_URLS);
      return;
    }

    // ── Use bypassHttp (no interceptors) so a 401 from the logout endpoint
    //    never triggers the token-refresh interceptor, which would create an
    //    infinite loop or race condition that leaves the loader stuck. ──────────
    this.bypassHttp.post(`${environment.ssoApiUrl}sso/logout?sessionId=${ssoSession}`, {})
      .subscribe({
        next: async (res: any) => {
          const logoutUrls = res.silentLogoutUrls?.length ? res.silentLogoutUrls : this.FALLBACK_SILENT_LOGOUT_URLS;
          await performFinalCleanup(logoutUrls);
        },
        error: async (err: any) => {
          // 401 = session already expired on backend → still perform full local cleanup
          console.error("SSO Logout API failed (status:", err?.status, "), forcing local cleanup.");
          await performFinalCleanup(this.FALLBACK_SILENT_LOGOUT_URLS);
        }
      });
  }

  listenForLocalStorageLogout(onLogout: () => void) {
    window.addEventListener('storage', (event) => {
      if (event.key === 'token' && event.newValue === null) {
        onLogout();
      }
    });
  }

  setToken(tokenDetail) {
    localStorage.setItem('token', JSON.stringify(tokenDetail));
  }

  setId(companyId: string) {
    localStorage.setItem('id', companyId);
    console.log(companyId);

  };

  setRememberDetails(details: any): void {
    localStorage.setItem('login', JSON.stringify(details));
    console.log(details);

  }

  // getId(): string {
  //   const id = localStorage.getItem('id');
  //   return id ? JSON.parse(id) : ``;

  // }

  getId(): string {
    const id = localStorage.getItem('id');
    return id;
  };

  getLoginDetail(): any {
    const loginDetail = localStorage.getItem('login');
    return loginDetail ? JSON.parse(loginDetail) : ``;
  }

  getToken(): string {
    const token = localStorage.getItem('token');
    return token ? JSON.parse(token)?.token?.token : ``;

    // return token ? token?.token.token : ``;
  }

  // getSessionDetail() {
  //   return localStorage.getItem('token') ? JSON.parse(localStorage.getItem('token')) : null;
  // }

  getSessionDetail() {
    return localStorage.getItem('token') ? JSON.parse(localStorage.getItem('token')) : ``;
  }
  removeToken() {
    localStorage.removeItem("token");
  };
  removeId() {
    localStorage.removeItem("id");
  };
  removeLoginDetails() {
    localStorage.removeItem("login");
  };
  isAuthenticatedId(): boolean {
    const id = this.getId();
    return id?.length > 0 ? true : false;
  }
  isAuthenticated(): boolean {
    const token = this.getToken();
    return token?.length > 0 ? true : false;
  }
}
